import type { InfiniteData, QueryClient } from "@tanstack/react-query";
import { preflightKeys } from "@/lib/queries";
import type {
  PreflightReportDetail,
  PreflightReportListPage,
  PreflightReportSummary,
} from "../types/preflight.types";

type ListData = InfiniteData<PreflightReportListPage>;

/** Apply a partial patch to a report wherever it appears in the detail + list caches. */
export function patchReportInCaches(
  queryClient: QueryClient,
  id: string,
  patch: Partial<PreflightReportSummary>,
): void {
  // Scan all detail cache entries for this id across every tenant key.
  // Using setQueriesData with a predicate avoids coupling to the current active
  // tenant, which could miss the original tenant's cache if a tenant switch
  // races with the mutation.
  queryClient.setQueriesData<PreflightReportDetail>(
    {
      queryKey: preflightKeys.details(),
      predicate: (query) => query.queryKey[4] === id,
    },
    (prev) => (prev ? { ...prev, ...patch } : prev),
  );

  queryClient.setQueriesData<ListData>(
    { queryKey: preflightKeys.lists() },
    (prev) =>
      prev
        ? {
            ...prev,
            pages: prev.pages.map((page) => ({
              ...page,
              items: page.items.map((item) =>
                item.id === id ? { ...item, ...patch } : item,
              ),
            })),
          }
        : prev,
  );
}

/** Remove a report from every list cache (used after delete). */
export function removeReportFromCaches(
  queryClient: QueryClient,
  id: string,
): void {
  queryClient.setQueriesData<ListData>(
    { queryKey: preflightKeys.lists() },
    (prev) =>
      prev
        ? {
            ...prev,
            pages: prev.pages.map((page) => ({
              ...page,
              items: page.items.filter((item) => item.id !== id),
            })),
          }
        : prev,
  );
}
